import React from 'react';
import { TaxPlanSummary as Summary } from '../../hooks/useTaxPlan';
import { formatAUD } from '../../utils/formatters';
import { InfoTooltip } from '../ui/InfoTooltip';

// Mirrors the Portfolio stat cards (src/components/Dashboard/Dashboard.tsx) so the
// Tax Plan summary reads as the same family of cards. Uses the mode-aware
// --overlay / --color-stat-label vars rather than hard-coded hex so it adapts to
// the light/dark toggle.
const cardStyle: React.CSSProperties = {
  backgroundColor: 'rgb(var(--overlay) / 0.025)',
  border: '0.5px solid rgb(var(--overlay) / 0.06)',
  borderRadius: 10,
  padding: '14px 16px',
};

const labelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.08em',
  color: 'var(--color-stat-label)',
};

const valueStyle: React.CSSProperties = {
  fontSize: 19,
  fontWeight: 500,
  letterSpacing: '-0.01em',
  fontVariantNumeric: 'tabular-nums',
};

export function TaxPlanSummary({ summary }: { summary: Summary }) {
  const gainPositive = summary.totalGain >= 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {/* Total proceeds */}
      <div style={cardStyle}>
        <p className="uppercase mb-1.5" style={labelStyle}>Total proceeds</p>
        <p className="leading-tight text-slate-100" style={valueStyle}>
          {formatAUD(summary.totalProceeds)}
        </p>
      </div>

      {/* Total cost basis */}
      <div style={cardStyle}>
        <div className="flex items-center gap-1.5 mb-1.5">
          <p className="uppercase" style={labelStyle}>Total cost basis</p>
          <InfoTooltip text="What you paid for an asset, including brokerage and fees. Your capital gain is the sale price minus this." />
        </div>
        <p className="leading-tight text-slate-100" style={valueStyle}>
          {formatAUD(summary.totalCostBasis)}
        </p>
      </div>

      {/* Total estimated gain */}
      <div style={cardStyle}>
        <p className="uppercase mb-1.5" style={labelStyle}>Total estimated gain</p>
        <p
          className={`leading-tight ${gainPositive ? 'text-emerald-400' : 'text-red-400'}`}
          style={valueStyle}
        >
          {formatAUD(summary.totalGain, { sign: true })}
        </p>
      </div>

      {/* Estimated CGT owed */}
      <div style={cardStyle}>
        <p className="uppercase mb-1.5" style={labelStyle}>Estimated CGT owed</p>
        <p className="leading-tight" style={{ ...valueStyle, color: '#d4b483' }}>
          {formatAUD(summary.totalCGT)}
        </p>
        <p className="text-[11px] mt-1.5 opacity-80 text-slate-500">
          {summary.discountQualifyingCount} of {summary.count} estimate
          {summary.count !== 1 ? 's' : ''} qualify for CGT discount
        </p>
      </div>
    </div>
  );
}
